import { useEffect, useId, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import type { AudioConsentMetadata } from '@/types'
import {
  createConsentMetadata,
  RECORDING_CONSENT_ACKNOWLEDGEMENT,
  RECORDING_CONSENT_NOTICE,
} from '@/lib/recordingConsent'

interface RecordConsentModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (metadata: AudioConsentMetadata) => void
}

export function RecordConsentModal({
  isOpen,
  onClose,
  onConfirm,
}: RecordConsentModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const titleId = useId()
  const noticeId = useId()
  const acknowledgementId = useId()

  useEffect(() => {
    if (!isOpen) {
      return
    }

    closeButtonRef.current?.focus()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    const handleTab = (event: KeyboardEvent) => {
      if (!modalRef.current || event.key !== 'Tab') return

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusableElements.length === 0) return

      const first = focusableElements[0]
      const last = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement

      if (!modalRef.current.contains(activeElement)) {
        event.preventDefault()
        first?.focus()
        return
      }

      if (event.shiftKey && activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('keydown', handleTab)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('keydown', handleTab)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen) {
    return null
  }

  const handleConfirm = () => {
    onConfirm(createConsentMetadata(true))
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={`${noticeId} ${acknowledgementId}`}
    >
      <div
        ref={modalRef}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-3xl border border-navy-light bg-navy-light px-4 pb-6 pt-4 shadow-2xl sm:rounded-3xl sm:px-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-white/15 sm:hidden" />
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-xl font-semibold text-white">
              Confirm recording consent
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="rounded-lg bg-white/5 p-2 text-slate-300 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-neon-cyan"
            aria-label="Close consent dialog"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div
            id={noticeId}
            className="rounded-2xl border border-neon-cyan/25 bg-neon-cyan/10 px-4 py-4 text-sm text-white"
          >
            {RECORDING_CONSENT_NOTICE}
          </div>
          <div
            id={acknowledgementId}
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-slate-100"
          >
            {RECORDING_CONSENT_ACKNOWLEDGEMENT}
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-white/10 px-4 py-3 text-sm font-semibold text-slate-200 transition hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-neon-cyan"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="flex-1 rounded-xl bg-orange px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-dark focus:outline-none focus:ring-2 focus:ring-orange"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
}
